/**
 * Walkthrough mode — keyframe playback paired with per-keyframe
 * voice-over transcripts. The coach hits "Walk" and the play narrates
 * itself moment by moment: progress advances along the keyframe
 * timeline, and as the active keyframe changes the matching
 * `TacticsVoiceover.transcript` fires through whatever speak()
 * callback the caller wires in (usually the existing TTS hook).
 *
 * This hook is intentionally render-target-agnostic: it returns the
 * sampled `positions` map (same shape `samplePositions` already
 * yields) so the 2D + 3D boards can pipe it into their token render
 * override without per-surface bespoke playback logic. When a board
 * adopts Walkthrough mode, it sources its token positions from
 * `positions` while `isPlaying` (or any time `progress > 0`); the
 * existing single-arrow playback continues to work because the boards
 * still own arrows + reduced motion + their own RAF for arrow curves.
 *
 * Branch resolution mirrors `samplePositions`: a branchId switches the
 * playback path to the alternate keyframes rooted at the branch's
 * parent keyframe. Once a branch is selected, progress walks the same
 * remapped [0, 1] window the scrubber uses — branch keyframe markers
 * land where they're authored within that window.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  TacticsBranch,
  TacticsKeyframe,
  TacticsVoiceover,
} from '@4cm/core/tactics';
import {
  samplePositions,
  type PlayerKey,
  type SampledPosition,
} from './tacticsKeyframes';

/**
 * Approximate dwell time per keyframe segment. Scaled by keyframe
 * count so a longer play reads at a consistent narration pace rather
 * than racing through. 2.5s × N is enough for a sentence of TTS to
 * land between transitions on system speech; cloud TTS chunks may
 * spill over but the next transcript fires correctly regardless.
 */
const MS_PER_KEYFRAME = 2500;

/** Minimum duration so a 2-keyframe play still feels animated rather
 *  than snapping. */
const MIN_DURATION_MS = 1500;

/** Minimal speak-shape — we only need a `speak(text)`. Keeping it
 *  this loose means the caller can pass `useBrainAutoSpeak().speak`,
 *  a raw `useTextToSpeech().speak`, or a mock in tests. */
export interface WalkthroughSpeaker {
  speak: (text: string) => void;
}

export interface UseWalkthroughPlaybackOptions {
  /** Main-sequence keyframes for the active layout. */
  keyframes: TacticsKeyframe[];
  /** Optional branches; selectable via setBranch. */
  branches?: TacticsBranch[];
  /** Per-keyframe voice-over transcripts. Lookup by keyframeId. */
  voiceovers?: TacticsVoiceover[];
  /** Speak handle — usually `useTextToSpeech().speak` or
   *  `useBrainAutoSpeak().speak`. When omitted the hook silently
   *  no-ops on transcript fires (useful in unit tests + reduced-
   *  motion paths). */
  speaker?: WalkthroughSpeaker;
  /** Mirror of the OS-level `prefers-reduced-motion`. When true,
   *  `play()` jumps progress straight to 1 and emits every
   *  transcript in order so the coach still gets the narration. */
  reducedMotion?: boolean;
  /** Master mute. When false, transcripts are not spoken (positions
   *  still animate). Defaults to true so existing callers don't have
   *  to opt-in. */
  ttsEnabled?: boolean;
}

export interface UseWalkthroughPlaybackResult {
  /** RAF loop running. */
  isPlaying: boolean;
  /** Progress along the active path, [0, 1]. */
  progress: number;
  /** Id of the keyframe whose window currently contains `progress`.
   *  Null when there are no keyframes or progress is before the first
   *  keyframe's window. */
  activeKeyframeId: string | null;
  /** Currently selected branch id; undefined = main sequence. */
  activeBranchId: string | undefined;
  /** Sampled positions at `progress` — same shape `samplePositions`
   *  yields. Pipe directly into the board's token render override. */
  positions: Map<PlayerKey, SampledPosition>;
  play: () => void;
  pause: () => void;
  reset: () => void;
  /** Coach-driven scrub. Pauses any RAF in flight and lets the
   *  pointer drive progress directly. */
  setProgress: (next: number) => void;
  /** Switch the playback path. Reset progress to 0 so the branch
   *  walks from the start of its remapped window. */
  setBranch: (branchId: string | undefined) => void;
}

/**
 * Resolve the ordered timeline (main keyframes — or main prefix +
 * branch keyframes) the playback should walk. Each entry retains the
 * `t` value `samplePositions` uses so callers can compute keyframe
 * boundaries without duplicating the timeline math.
 */
function buildTimeline(
  keyframes: TacticsKeyframe[],
  branches: TacticsBranch[] | undefined,
  branchId: string | undefined,
): Array<{ id: string; t: number }> {
  if (!keyframes || keyframes.length === 0) return [];
  const branch = branchId
    ? (branches ?? []).find((b) => b.id === branchId) ?? null
    : null;
  if (!branch) {
    return [...keyframes]
      .sort((a, b) => a.t - b.t)
      .map((k) => ({ id: k.id, t: k.t }));
  }
  const sorted = [...keyframes].sort((a, b) => a.t - b.t);
  const parentIdx = sorted.findIndex((k) => k.id === branch.parentKeyframeId);
  if (parentIdx < 0) {
    return sorted.map((k) => ({ id: k.id, t: k.t }));
  }
  const prefix = sorted.slice(0, parentIdx + 1);
  const parentT = sorted[parentIdx].t;
  const branchSpan = Math.max(0, 1 - parentT);
  const branchSorted = [...branch.keyframes].sort((a, b) => a.t - b.t);
  const remapped = branchSorted.map((k) => ({
    id: k.id,
    t: parentT + branchSpan * Math.max(0, Math.min(1, k.t)),
  }));
  return [...prefix.map((k) => ({ id: k.id, t: k.t })), ...remapped];
}

/**
 * Given the timeline + a progress value, return the id of the
 * keyframe whose window contains that progress. We use a "previous
 * keyframe whose `t` is ≤ progress" rule so each keyframe is the
 * active one from the moment it's reached until the next keyframe
 * fires. The first keyframe is active at progress=0.
 */
function findActiveKeyframeId(
  timeline: Array<{ id: string; t: number }>,
  progress: number,
): string | null {
  if (timeline.length === 0) return null;
  const clamped = Math.max(0, Math.min(1, progress));
  let activeId = timeline[0].id;
  for (const entry of timeline) {
    if (entry.t <= clamped) activeId = entry.id;
    else break;
  }
  return activeId;
}

export function useWalkthroughPlayback({
  keyframes,
  branches,
  voiceovers,
  speaker,
  reducedMotion = false,
  ttsEnabled = true,
}: UseWalkthroughPlaybackOptions): UseWalkthroughPlaybackResult {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgressState] = useState(0);
  const [activeBranchId, setActiveBranchId] = useState<string | undefined>(undefined);

  // Track the last keyframe id we narrated so we don't re-fire on
  // every render — only when the active keyframe transitions.
  const lastSpokenKeyframeIdRef = useRef<string | null>(null);

  // Stable map from keyframe id → transcript for O(1) lookup as the
  // active keyframe changes during playback.
  const transcriptByKeyframe = useMemo(() => {
    const out = new Map<string, string>();
    for (const v of voiceovers ?? []) {
      if (v.transcript && v.transcript.trim().length > 0) {
        out.set(v.keyframeId, v.transcript);
      }
    }
    return out;
  }, [voiceovers]);

  const timeline = useMemo(
    () => buildTimeline(keyframes, branches, activeBranchId),
    [keyframes, branches, activeBranchId],
  );

  const activeKeyframeId = useMemo(
    () => findActiveKeyframeId(timeline, progress),
    [timeline, progress],
  );

  const positions = useMemo(
    () => samplePositions(keyframes, progress, activeBranchId, branches),
    [keyframes, progress, activeBranchId, branches],
  );

  // Latest-value refs for the speaker + transcript lookup so the RAF
  // tick callback fires the right transcript without re-creating the
  // tick closure on every render (which would tear down + rebuild
  // the playback loop on each keystroke / state change).
  const speakerRef = useRef(speaker);
  speakerRef.current = speaker;
  const ttsEnabledRef = useRef(ttsEnabled);
  ttsEnabledRef.current = ttsEnabled;
  const transcriptByKeyframeRef = useRef(transcriptByKeyframe);
  transcriptByKeyframeRef.current = transcriptByKeyframe;
  const timelineRef = useRef(timeline);
  timelineRef.current = timeline;

  /** Fire the transcript for the given keyframe id, suppressing a
   *  repeat fire for the same keyframe and skipping when TTS is
   *  muted or no speaker is wired. */
  const speakTranscript = useCallback((kfId: string | null) => {
    if (!kfId) return;
    if (!ttsEnabledRef.current) return;
    const sp = speakerRef.current;
    if (!sp) return;
    if (lastSpokenKeyframeIdRef.current === kfId) return;
    const text = transcriptByKeyframeRef.current.get(kfId);
    lastSpokenKeyframeIdRef.current = kfId;
    if (!text) return;
    sp.speak(text);
  }, []);

  // Duration scales with keyframe count so 2-keyframe and 5-keyframe
  // plays both feel paced rather than rushed / dragging.
  const durationMs = useMemo(() => {
    if (timeline.length <= 1) return MIN_DURATION_MS;
    return Math.max(MIN_DURATION_MS, MS_PER_KEYFRAME * timeline.length);
  }, [timeline]);

  const rafRef = useRef<number | null>(null);
  // Cancel any in-flight RAF — used by pause / reset / unmount.
  const cancelRaf = useCallback(() => {
    if (rafRef.current !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = null;
  }, []);

  // RAF loop. Mirrors the boards' existing `playbackProgress` pattern
  // (start ts derived from current progress so resume picks up where
  // it left off). The tick advances `progress` state AND fires the
  // matching transcript on each keyframe boundary directly from
  // within the tick — this is more robust than gating speaks on a
  // useEffect that only sees the final state when fake timers fire
  // multiple ticks synchronously (tests + production behave the
  // same).
  useEffect(() => {
    if (!isPlaying) return;
    if (typeof requestAnimationFrame !== 'function') {
      // No RAF (e.g. test env without happy-dom polyfill): snap to end.
      setProgressState(1);
      setIsPlaying(false);
      return;
    }
    const startTs =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now() - progress * durationMs
        : Date.now() - progress * durationMs;
    const tick = () => {
      const now =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();
      const elapsed = now - startTs;
      const next = Math.min(elapsed / durationMs, 1);
      // Fire keyframe transcript inline before scheduling next tick.
      // Reads `timelineRef.current` so we always see the active
      // branch's timeline even when branch state has just changed.
      const activeKfId = findActiveKeyframeId(timelineRef.current, next);
      speakTranscript(activeKfId);
      setProgressState(next);
      if (next >= 1) {
        setIsPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelRaf();
    // We intentionally don't depend on `progress` — the start ts bakes
    // it on each isPlaying→true transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, durationMs, cancelRaf, speakTranscript]);

  // Cancel RAF on unmount so a navigated-away page doesn't leave a
  // ticker running.
  useEffect(() => () => cancelRaf(), [cancelRaf]);

  const play = useCallback(() => {
    if (timeline.length === 0) return; // empty layout: no-op
    if (reducedMotion) {
      // Emit every transcript in order, then snap to the end.
      lastSpokenKeyframeIdRef.current = null;
      if (ttsEnabled && speaker) {
        for (const entry of timeline) {
          const text = transcriptByKeyframe.get(entry.id);
          if (text && text.trim().length > 0) speaker.speak(text);
        }
      }
      // Mark the last keyframe as the "spoken" anchor so a follow-up
      // scrub doesn't immediately re-narrate the end keyframe.
      lastSpokenKeyframeIdRef.current = timeline[timeline.length - 1].id;
      setProgressState(1);
      setIsPlaying(false);
      return;
    }
    // Reset the spoken-anchor so the upcoming first-keyframe fires
    // even if we replay the same play (otherwise lastSpokenId would
    // still match k0 and suppress it).
    lastSpokenKeyframeIdRef.current = null;
    // If we were already at the end, rewind so play means "play".
    const startProgress = progress >= 1 ? 0 : progress;
    if (progress >= 1) setProgressState(0);
    // Fire the first active keyframe's transcript immediately so the
    // narration starts with the play, not on the first RAF tick.
    const firstKfId = findActiveKeyframeId(timeline, startProgress);
    speakTranscript(firstKfId);
    setIsPlaying(true);
  }, [timeline, reducedMotion, ttsEnabled, speaker, transcriptByKeyframe, progress, speakTranscript]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    cancelRaf();
  }, [cancelRaf]);

  const reset = useCallback(() => {
    setIsPlaying(false);
    cancelRaf();
    setProgressState(0);
    lastSpokenKeyframeIdRef.current = null;
  }, [cancelRaf]);

  const setProgress = useCallback(
    (next: number) => {
      setIsPlaying(false);
      cancelRaf();
      setProgressState(Math.max(0, Math.min(1, next)));
    },
    [cancelRaf],
  );

  const setBranch = useCallback(
    (branchId: string | undefined) => {
      setIsPlaying(false);
      cancelRaf();
      setActiveBranchId(branchId);
      setProgressState(0);
      lastSpokenKeyframeIdRef.current = null;
    },
    [cancelRaf],
  );

  return {
    isPlaying,
    progress,
    activeKeyframeId,
    activeBranchId,
    positions,
    play,
    pause,
    reset,
    setProgress,
    setBranch,
  };
}
